import {
  ArrowForwardRounded,
  ErrorOutlineRounded,
  PriorityHighRounded,
  ScheduleRounded,
  FlagOutlined,
  LocalShippingOutlined,
  FilterAltOutlined,
} from "@mui/icons-material";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import api from "Service/api/api";
import { useAppDispatch } from "Redux/store";
import { selectVehicleList } from "Redux/Vehicle/slice";
import { getVehicleList } from "Redux/Vehicle/reducer";
import { ICurrentVehicle } from "Shared/Types/vehicle";

import { ISSUES } from "Shared/Constants/routes";
import { LIMIT_VEHICLES } from "Shared/Constants/app";
import { getRoutWithId } from "Shared/Constants/routeIdenicator";
import ModalOptions from "Components/UI/ModalFilters/ModalOptions";
import { SkeletonStatus, SkeletonText } from "Components/Dashboard/Skeleton";
import {
  issueRows,
  IssueRow,
  isOverdueIssue,
  prioritizeIssues,
  needsIssueReview,
  filterIssueFocus,
  IssueFocus,
} from "./issueRows";
import { issueScope } from "./issueScope";
import styles from "./IssuesOverview.module.scss";

const PAGE_SIZE = 6;
const label = (value: string) => value.replace(/_/g, " ");

// Same markup as a full loaded page (priority line, pagination), so the list
// keeps its height on arrival.
const IssueRowsSkeleton = () => (
  <div aria-hidden="true">
    <ul className={styles.rows}>
      {Array.from({ length: PAGE_SIZE }, (_, index) => (
        <li key={index}>
          <span className={styles.issueIcon} />
          <div className={styles.issue}>
            <strong>
              <SkeletonText width="11em" />
            </strong>
            <span>
              <SkeletonText width="9em" />
            </span>
          </div>
          <span className={styles.status}>
            <SkeletonText width="4em" />
            <small>
              <SkeletonText width="6em" />
            </small>
          </span>
          <span className={styles.date}>
            <SkeletonText width="7em" />
          </span>
          <span className={styles.openLink} />
        </li>
      ))}
    </ul>
    <footer className={styles.footer}>
      <span>
        <SkeletonText width="14em" />
      </span>
      <div>
        <button type="button" tabIndex={-1} disabled>
          Previous
        </button>
        <button type="button" tabIndex={-1} disabled>
          Next
        </button>
      </div>
    </footer>
  </div>
);

const IssuesOverview = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const vehicles = useSelector(selectVehicleList) || [];
  const { from, to, periodView } = issueScope(searchParams);
  const vehicle = searchParams.get("issues_vehicle");
  const [data, setData] = useState<IssueRow[]>([]);
  const [scanLimit, setScanLimit] = useState(500);
  const [coverage, setCoverage] = useState({ loaded: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(0);
  const [focus, setFocus] = useState<IssueFocus>("all");
  const validRange = !periodView || Boolean(from && to && from <= to);

  useEffect(() => {
    let active = true;
    setData([]);
    setError(false);
    setPage(0);
    setFocus("all");
    if (!validRange) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const load = async () => {
      if (periodView) {
        const response = await api.dashboard.getDashboardIssuesCalendar({
          vehicle,
          start_date: from,
          end_date: to,
        });
        if (active) {
          setData(issueRows(response.data.vehicles));
          setCoverage({ loaded: 0, total: 0 });
        }
        return;
      }
      // The API's status=open excludes overdue and undated open cases.
      // Read actual statuses from unfiltered pages so those cases stay visible.
      const collected: IssueRow[] = [];
      let offset = 0;
      let total = 0;
      while (active && offset < scanLimit) {
        const { data: response } = await api.issue.getIssueList({
          vehicle,
          limit: 100,
          offset,
        });
        if (!active) return;
        total = response.count;
        collected.push(
          ...response.results.map((issue) => ({
            id: issue.id,
            summary: issue.summary,
            status: issue.status || "unknown",
            priority: issue.priority || "no_priority",
            reported_date: issue.reported_date,
            due_date: issue.due_date || null,
            vehicleId: issue.vehicle?.id || "",
            vehicleName: issue.vehicle?.name || "Vehicle unavailable",
          })),
        );
        offset += response.results.length;
        if (!response.next || !response.results.length || offset >= total)
          break;
      }
      if (active) {
        setData([
          ...new Map(collected.map((issue) => [issue.id, issue])).values(),
        ]);
        setCoverage({ loaded: offset, total });
      }
    };
    load()
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [from, to, vehicle, validRange, retry, periodView, scanLimit]);

  const rows = useMemo(() => prioritizeIssues(data), [data]);
  const scoped = rows.filter((issue) =>
    periodView
      ? status === "all" || issue.status === status
      : needsIssueReview(issue),
  );
  const filtered = filterIssueFocus(scoped, focus);
  const focusOptions = [
    { key: "all" as const, label: "All", Icon: FilterAltOutlined },
    { key: "critical" as const, label: "Critical", Icon: PriorityHighRounded },
    { key: "overdue" as const, label: "Overdue", Icon: ScheduleRounded },
    { key: "high" as const, label: "High priority", Icon: FlagOutlined },
  ];
  const start = page * PAGE_SIZE;
  const incomplete = !periodView && coverage.loaded < coverage.total;
  const changeView = (value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("issues_view", value);
    setSearchParams(next);
    setPage(0);
  };
  const updateDate = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set(key, value);
    next.set("issues_date_range", "custom");
    next.set("issues_view", "period");
    setSearchParams(next);
  };

  return (
    <section
      className={styles.overview}
      aria-labelledby="issues-overview-title"
    >
      <header className={styles.heading}>
        <div>
          <h3 id="issues-overview-title">
            <ErrorOutlineRounded aria-hidden="true" />
            Issues
          </h3>
          <p>
            {periodView
              ? `Reported ${from} to ${to}`
              : "Needs review · all reported dates"}
          </p>
        </div>
        <Link to={ISSUES}>
          All issues <ArrowForwardRounded aria-hidden="true" />
        </Link>
      </header>
      {!error && validRange && (
        <>
          <div className={styles.summary}>
            <strong>
              {loading ? <SkeletonText width="1.2em" /> : scoped.length}
              {!loading && incomplete && <small>+</small>}
            </strong>
            <div>
              <span>
                {periodView ? "issues in this view" : "issues to review"}
              </span>
              <small>
                {loading ? (
                  <SkeletonText width="10em" />
                ) : incomplete ? (
                  "In loaded records · more to check"
                ) : vehicle ? (
                  "Selected vehicle"
                ) : (
                  "All vehicles"
                )}
              </small>
            </div>
            <ErrorOutlineRounded aria-hidden="true" />
          </div>
          <div
            className={styles.focusFilters}
            aria-label="Filter issues by urgency"
          >
            {focusOptions.map(({ key, label, Icon }) => (
              <button
                key={key}
                type="button"
                data-focus={key}
                aria-pressed={focus === key}
                disabled={loading}
                onClick={() => {
                  setFocus(key);
                  setPage(0);
                }}
              >
                <Icon aria-hidden="true" />
                <strong>
                  {loading ? (
                    <SkeletonText width="1ch" />
                  ) : (
                    filterIssueFocus(scoped, key).length
                  )}
                </strong>
                <span>{label}</span>
              </button>
            ))}
          </div>
        </>
      )}
      <div className={styles.filters}>
        <label>
          View
          <select
            value={periodView ? "period" : "attention"}
            onChange={(e) => changeView(e.target.value)}
          >
            <option value="attention">Needs review · all dates</option>
            <option value="period">Reported in a period</option>
          </select>
        </label>
        {periodView && (
          <>
            <label>
              From
              <input
                type="date"
                value={from}
                onChange={(e) => updateDate("issues_from", e.target.value)}
              />
            </label>
            <label>
              To
              <input
                type="date"
                value={to}
                onChange={(e) => updateDate("issues_to", e.target.value)}
              />
            </label>
          </>
        )}
        <div className={styles.vehicleFilter}>
          <ModalOptions<ICurrentVehicle>
            options={vehicles}
            fetchOptions={(search) => {
              dispatch(
                getVehicleList({ search, limit: LIMIT_VEHICLES, offset: 0 }),
              );
            }}
            type="issues_vehicle"
            title="Vehicle"
            zIndex
          />
        </div>
        {periodView && (
          <label>
            Status
            <select
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(0);
              }}
            >
              <option value="all">All statuses</option>
              <option value="open">Open</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
              <option value="done">Done</option>
            </select>
          </label>
        )}
      </div>
      {!periodView &&
        !loading &&
        !error &&
        rows.some((issue) => issue.status === "unknown") && (
          <p className={styles.message} role="status">
            Some records do not include a status. They remain visible for
            verification; open totals cannot be confirmed.
          </p>
        )}
      {!validRange ? (
        <p className={styles.message} role="alert">
          Choose a start date on or before the end date.
        </p>
      ) : loading ? (
        <>
          <SkeletonStatus>Loading issues…</SkeletonStatus>
          <IssueRowsSkeleton />
        </>
      ) : error ? (
        <div className={styles.message} role="alert">
          <p>Issues could not be loaded.</p>
          <button type="button" onClick={() => setRetry((value) => value + 1)}>
            Try again
          </button>
        </div>
      ) : !filtered.length ? (
        <p className={styles.message}>
          {periodView
            ? "No issues match this period and these filters."
            : incomplete
              ? "No issues needing review found in the records checked so far."
              : focus !== "all"
                ? "No issues match this urgency filter."
                : "No open or unverified issues match this vehicle scope."}
        </p>
      ) : (
        <>
          <ul className={styles.rows}>
            {filtered.slice(start, start + PAGE_SIZE).map((issue) => (
              <li key={issue.id}>
                <span
                  className={styles.issueIcon}
                  data-priority={issue.priority}
                  data-overdue={isOverdueIssue(issue)}
                >
                  {isOverdueIssue(issue) ? (
                    <ScheduleRounded aria-hidden="true" />
                  ) : issue.priority === "critical" ? (
                    <PriorityHighRounded aria-hidden="true" />
                  ) : (
                    <FlagOutlined aria-hidden="true" />
                  )}
                </span>
                <div className={styles.issue}>
                  <Link to={getRoutWithId(ISSUES, issue.id)}>
                    {issue.summary || "Untitled issue"}
                  </Link>
                  <span>
                    <LocalShippingOutlined aria-hidden="true" />
                    {issue.vehicleName}
                  </span>
                </div>
                <span className={styles.status} data-status={issue.status}>
                  {isOverdueIssue(issue)
                    ? "Overdue"
                    : issue.status === "unknown" || !issue.status
                      ? "Status unavailable"
                      : label(issue.status)}
                  {issue.priority && issue.priority !== "no_priority" && (
                    <small> · {label(issue.priority)}</small>
                  )}
                </span>
                <span className={styles.date}>
                  <ScheduleRounded aria-hidden="true" />
                  <time dateTime={issue.reported_date}>
                    {issue.reported_date.slice(0, 10)}
                  </time>
                </span>
                <Link
                  className={styles.openLink}
                  to={getRoutWithId(ISSUES, issue.id)}
                  aria-label={`Open issue: ${issue.summary || "Untitled issue"}`}
                >
                  <ArrowForwardRounded aria-hidden="true" />
                </Link>
              </li>
            ))}
          </ul>
          <footer className={styles.footer}>
            <span>
              {start + 1}–{Math.min(start + PAGE_SIZE, filtered.length)} of{" "}
              {filtered.length}{" "}
              {incomplete
                ? "matching issues in loaded records"
                : "matching issues"}
            </span>
            {filtered.length > PAGE_SIZE && (
              <div>
                <button
                  type="button"
                  disabled={page === 0}
                  onClick={() => setPage((value) => value - 1)}
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={start + PAGE_SIZE >= filtered.length}
                  onClick={() => setPage((value) => value + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </footer>
        </>
      )}
      {!loading && !error && incomplete && (
        <div className={styles.coverage} role="status">
          <p>
            Checked {coverage.loaded} of {coverage.total} issue records. Older
            open cases may be outside this loaded set.
          </p>
          <button
            type="button"
            onClick={() => setScanLimit((value) => value + 500)}
          >
            Load more records
          </button>
        </div>
      )}
    </section>
  );
};

export default IssuesOverview;
